import Heading from '@atlaskit/heading';
import { Box, Grid, media, Stack, Text, xcss } from '@atlaskit/primitives';
import { useMemo } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import { Card } from '../common/card';
import { getAvailableCapabilities, getComingSoonCapabilities } from '../utils/capabilities';
import {
	isBitbucketConnectedOnTenant,
	isGitHubConnectedAndConfiguredOnTenant,
} from '../utils/integrations';

import type { capabilities_IntegrationsConfigStates$key } from './__generated__/capabilities_IntegrationsConfigStates.graphql';

const capabilitiesStyles = xcss({
	paddingTop: 'space.200',
});

const capabilitiesSectionWrapper = xcss({
	paddingTop: 'space.500',
});

const responsiveStyles = xcss({
	[media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
	[media.above.sm]: { gridTemplateColumns: 'repeat(2, 1fr)' },
	[media.above.md]: {
		gridTemplateColumns: 'repeat(3, 1fr)',
	},
});

export const Capabilities = ({
	isUserProductAdmin = true,
	hasScmIntegrations = false,
	integrationsConfigStatesFragment,
}: {
	integrationsConfigStatesFragment: capabilities_IntegrationsConfigStates$key | undefined | null;
	isUserProductAdmin?: boolean;
	hasScmIntegrations?: boolean;
}) => {
	const integrations = useFragment(
		graphql`
			fragment capabilities_IntegrationsConfigStates on JiraAppConfigStateEdge
			@relay(plural: true) {
				node @required(action: THROW) {
					id @required(action: THROW)
					appDisplayName
					appIconLink
					config @required(action: THROW) {
						edges @required(action: THROW) {
							node {
								status
								configureLink
							}
						}
					}
				}
			}
		`,
		integrationsConfigStatesFragment,
	);

	const configStates = Object.fromEntries(
		(integrations || []).map((edge) => [
			edge.node.id,
			edge.node.config.edges[0]?.node?.status || 'NOT_CONFIGURED',
		]),
	);
	const availableCapabilities = useMemo(() => {
		return getAvailableCapabilities(
			isUserProductAdmin,
			hasScmIntegrations,
			isBitbucketConnectedOnTenant(configStates),
			isGitHubConnectedAndConfiguredOnTenant(configStates),
		);
	}, [configStates, hasScmIntegrations, isUserProductAdmin]);

	const comingSoonCapabilities = useMemo(() => {
		return getComingSoonCapabilities(
			isUserProductAdmin,
			hasScmIntegrations,
			isBitbucketConnectedOnTenant(configStates),
			isGitHubConnectedAndConfiguredOnTenant(configStates),
		);
	}, [configStates, hasScmIntegrations, isUserProductAdmin]);

	return (
		<Box xcss={capabilitiesSectionWrapper}>
			<Stack space="space.150">
				<Heading size="xlarge" testId="capabilities-section-heading">
					Agents
				</Heading>
				{!isUserProductAdmin && (
					<Text as="p">
						You must be a Rovo Dev Agents admin to enable, disable or manage agents on this site.
						For more information, talk to your site admin.
					</Text>
				)}
			</Stack>

			<Box xcss={capabilitiesStyles}>
				<Stack space="space.200">
					<Stack space="space.200">
						<Grid xcss={responsiveStyles} gap="space.200">
							{availableCapabilities.map((capability, index) => (
								<Card
									description={capability.description}
									icon={capability.icon}
									primaryAction={capability.primaryAction}
									secondaryAction={capability.secondaryAction}
									title={capability.title}
									lozenge={capability.lozenge}
									key={`capability-${index}`}
								/>
							))}
						</Grid>
					</Stack>
					<Stack space="space.200">
						<Text size="large" weight="medium">
							Coming soon
						</Text>
						<Grid xcss={responsiveStyles} gap="space.200">
							{comingSoonCapabilities.map((capability, index) => (
								<Card
									description={capability.description}
									icon={capability.icon}
									primaryAction={capability.primaryAction}
									secondaryAction={capability.secondaryAction}
									title={capability.title}
									lozenge={capability.lozenge}
									key={`capability-${index}`}
								/>
							))}
						</Grid>
					</Stack>
				</Stack>
			</Box>
		</Box>
	);
};
